'use client'

import React, { useEffect, useRef, useState } from 'react'
import styles from './progress_bar.module.scss'

interface Props {
    duration: number | null
    currentTime: number
    onSeekAction: (time: number) => void
}

export default function ProgressBar({ duration, currentTime, onSeekAction }: Props) {
    const [isDragging, setIsDragging] = useState(false)
    const [dragProgress, setDragProgress] = useState<number | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const activePointerIdRef = useRef<number | null>(null)

    const calculateTimeFromClientX = (clientX: number) => {
        if (!duration || duration <= 0 || !containerRef.current) return currentTime
        const rect = containerRef.current.getBoundingClientRect()
        if (rect.width === 0) return currentTime

        let percentage = (clientX - rect.left) / rect.width

        // Clamp percentage between 0 and 1
        percentage = Math.min(Math.max(percentage, 0), 1)
        const time = percentage * duration

        // Ensure time is valid and within bounds
        return isNaN(time) ? currentTime : Math.min(Math.max(time, 0), duration)
    }

    const handlePointerDown = (e: React.PointerEvent) => {
        if (!duration || duration <= 0 || !containerRef.current) return

        e.preventDefault()
        containerRef.current.focus()
        containerRef.current.setPointerCapture(e.pointerId)
        activePointerIdRef.current = e.pointerId

        const time = calculateTimeFromClientX(e.clientX)
        if (isNaN(time)) return

        setIsDragging(true)
        setDragProgress(time)
    }

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging) return
        if (activePointerIdRef.current !== e.pointerId) return
        if (!duration || duration <= 0) return

        const time = calculateTimeFromClientX(e.clientX)
        if (!isNaN(time)) setDragProgress(time)
    }

    const finishDrag = () => {
        if (dragProgress !== null) onSeekAction(dragProgress)
        setIsDragging(false)
        setDragProgress(null)
        activePointerIdRef.current = null
    }

    const handlePointerUp = (e: React.PointerEvent) => {
        if (activePointerIdRef.current !== e.pointerId) return
        finishDrag()
    }

    const handlePointerCancel = (e: React.PointerEvent) => {
        if (activePointerIdRef.current !== e.pointerId) return
        setIsDragging(false)
        setDragProgress(null)
        activePointerIdRef.current = null
    }

    const handleLostPointerCapture = (e: React.PointerEvent) => {
        if (activePointerIdRef.current !== e.pointerId) return
        // If capture is lost mid-drag, commit the latest value.
        if (isDragging) finishDrag()
    }

    useEffect(() => {
        if (!isDragging) return
        return () => {
            activePointerIdRef.current = null
        }
    }, [isDragging])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!duration || duration <= 0) return

        let newTime = currentTime

        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault()
                newTime = Math.max(0, currentTime - 5)
                onSeekAction(newTime)
                break
            case 'ArrowRight':
                e.preventDefault()
                newTime = Math.min(duration, currentTime + 5)
                onSeekAction(newTime)
                break
            case 'Home':
                e.preventDefault()
                onSeekAction(0)
                break
            case 'End':
                e.preventDefault()
                onSeekAction(duration)
                break
        }
    }

    const progress = isDragging && dragProgress !== null ? dragProgress : currentTime
    const progressPercentage = duration && duration > 0
        ? Math.min(Math.max((progress / duration) * 100, 0), 100)
        : 0
    const progressWidth = `${progressPercentage}%`
    console.log("Progress Bar - Current Time:", currentTime, "Drag Progress:", dragProgress, "Is Dragging:", isDragging, "Computed Progress:", progress, "progressWidth", progressWidth);

    return (
        <div className={styles.wrapper}>
            <div
                className={styles.container}
                ref={containerRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onLostPointerCapture={handleLostPointerCapture}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role="slider"
                aria-label="Video progress"
                aria-valuemin={0}
                aria-valuemax={duration ?? 0}
                aria-valuenow={duration && duration > 0 ? progress : 0}
                aria-valuetext={duration && duration > 0
                    ? `${Math.floor((progress / duration) * 100)}% played`
                    : 'Progress'}
            >
                <div className={styles.progress} style={{ width: progressWidth }}>
                    <div className={styles.thumbnail} />
                </div>
            </div>
        </div>
    )
}
